(() => {
  const initializeTabs = (root = document) => {
    const groups = root.querySelectorAll('[data-osmodi-product-tabs]');

    groups.forEach((group) => {
      if (group.dataset.osmodiTabsReady === 'true') return;
      group.dataset.osmodiTabsReady = 'true';
      const tabs = Array.from(group.querySelectorAll('[data-osmodi-tab]'));
      const panels = Array.from(group.querySelectorAll('[data-osmodi-panel]'));

      const activate = (name, focus = false) => {
        tabs.forEach((tab) => {
          const selected = tab.dataset.osmodiTab === name;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          if (selected && focus) tab.focus();
        });

        panels.forEach((panel) => {
          panel.hidden = panel.dataset.osmodiPanel !== name;
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab.dataset.osmodiTab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + direction + tabs.length) % tabs.length;
          activate(tabs[next].dataset.osmodiTab, true);
        });
      });
    });
  };

  const initializeVariantChoices = (root = document) => {
    root.querySelectorAll('.section-osmodi-oc-product').forEach((section) => {
      if (section.dataset.osmodiVariantsReady === 'true') return;
      section.dataset.osmodiVariantsReady = 'true';

      const choices = Array.from(section.querySelectorAll('[data-oc-variant-choice]'));
      const input = section.querySelector('[data-oc-variant-input]');
      const price = section.querySelector('[data-oc-price-current]');
      const compare = section.querySelector('[data-oc-price-compare]');
      const selectedSizes = Array.from(section.querySelectorAll('[data-oc-selected-size]'));
      const mainAdd = section.querySelector('[data-oc-main-add]');
      const mainAddLabel = mainAdd?.querySelector('[data-oc-main-add-label]');
      const stickyAdd = section.querySelector('[data-oc-sticky-add]');
      const stickyAddLabel = stickyAdd?.querySelector('span');
      const stickyPrice = section.querySelector('[data-oc-sticky-price]');
      const stickyBar = section.querySelector('[data-oc-stickybuy]');
      const buyAnchor = section.querySelector('[data-oc-buy-anchor]');

      if (stickyBar && buyAnchor) {
        let stickyFrame = 0;
        const syncStickyVisibility = () => {
          window.cancelAnimationFrame(stickyFrame);
          stickyFrame = window.requestAnimationFrame(() => {
            stickyBar.dataset.visible = String(buyAnchor.getBoundingClientRect().bottom < 0);
          });
        };
        window.addEventListener('scroll', syncStickyVisibility, { passive: true });
        window.addEventListener('resize', syncStickyVisibility, { passive: true });
        syncStickyVisibility();
      }

      const selectVariant = (choice) => {
        const id = choice.dataset.variantId;
        const title = choice.dataset.variantTitle || '';
        const available = choice.dataset.variantAvailable === 'true';
        if (!id || !input) return;

        input.value = id;
        choices.forEach((candidate) => {
          candidate.setAttribute('aria-current', candidate.dataset.variantId === id ? 'true' : 'false');
        });

        if (price) price.textContent = choice.dataset.variantPrice || '';
        if (stickyPrice) stickyPrice.textContent = choice.dataset.variantPrice || '';
        if (compare) {
          compare.textContent = choice.dataset.variantCompare || '';
          compare.hidden = !choice.dataset.variantCompare;
        }
        selectedSizes.forEach((selectedSize) => {
          selectedSize.textContent = title;
        });
        if (mainAdd) {
          mainAdd.disabled = !available;
        }
        if (mainAddLabel) mainAddLabel.textContent = available ? `Add ${title} to bag` : 'Sold out';
        if (stickyAdd) stickyAdd.disabled = !available;
        if (stickyAddLabel) stickyAddLabel.textContent = available ? `Add ${title}` : 'Sold out';

        const nextUrl = choice.dataset.variantUrl;
        if (nextUrl) window.history.replaceState({ variantId: id }, '', nextUrl);
        document.dispatchEvent(new CustomEvent('osmodi:variant-change', {
          detail: { id: Number(id), title, available }
        }));
      };

      choices.forEach((choice) => {
        choice.addEventListener('click', (event) => {
          event.preventDefault();
          selectVariant(choice);
        });
      });
    });
  };

  const enhanceProductDescriptions = (root = document) => {
    root.querySelectorAll('.oc-product-editorial__content').forEach((content) => {
      if (content.dataset.descriptionEnhanced === 'true') return;
      content.dataset.descriptionEnhanced = 'true';

      const children = Array.from(content.children);
      if (!children.length) return;

      const blocks = [];
      let block = null;
      children.forEach((child) => {
        if (!block || child.tagName === 'H2') {
          block = document.createElement('section');
          block.className = 'oc-description-block';
          blocks.push(block);
        }
        block.appendChild(child);
      });

      if (blocks[0] && !blocks[0].querySelector('h2')) {
        blocks[0].classList.add('oc-description-block--opening');
        blocks[0].dataset.descriptionKind = 'opening';
      }
      blocks.forEach((descriptionBlock, index) => {
        const heading = descriptionBlock.querySelector('h2');
        const headingText = heading?.textContent.trim().toLowerCase() || '';
        let kind = descriptionBlock.dataset.descriptionKind || 'general';
        if (headingText.startsWith('what does')) kind = 'smell';
        else if (headingText === 'featured notes') kind = 'notes';
        else if (headingText === 'fragrance family') kind = 'family';
        else if (headingText.startsWith('how long')) kind = 'longevity';
        else if (headingText === 'when to wear it') kind = 'wear';
        else if (headingText === 'what wearers say') kind = 'wearers';
        else if (headingText.startsWith('fragrances similar')) kind = 'similar';
        else if (headingText === 'authenticity') kind = 'authenticity';
        else if (headingText === 'product details') kind = 'details';

        descriptionBlock.dataset.descriptionKind = kind;
        descriptionBlock.classList.add(`oc-description-block--${kind}`);
        descriptionBlock.style.setProperty('--description-index', String(index + 1));

        if (kind === 'notes') {
          descriptionBlock.querySelectorAll('li').forEach((item) => {
            const layer = item.textContent.trim().split(':')[0].toLowerCase();
            if (!['top', 'heart', 'base'].includes(layer)) return;
            item.dataset.noteLayer = layer;
            const iconName = layer === 'top' ? 'drop' : layer === 'heart' ? 'star' : 'seal';
            const template = document.querySelector(`[data-oc-description-icon="${iconName}"]`);
            const icon = template?.content.firstElementChild?.cloneNode(true);
            if (icon) {
              const iconShell = document.createElement('span');
              iconShell.className = 'oc-note-layer-icon';
              iconShell.setAttribute('aria-hidden', 'true');
              iconShell.appendChild(icon);
              item.prepend(iconShell);
            }
          });
        }

        if (kind === 'family') {
          descriptionBlock.querySelectorAll('li').forEach((item) => {
            const match = item.textContent.match(/(\d+)%/);
            if (!match) return;
            const progress = document.createElement('progress');
            progress.max = 100;
            progress.value = Math.min(100, Number(match[1]));
            progress.setAttribute('aria-label', `${item.textContent.trim()} strength`);
            item.appendChild(progress);
          });
        }

        if (kind === 'details') {
          descriptionBlock.querySelectorAll('li').forEach((item) => {
            const text = item.textContent.trim();
            const separator = text.indexOf(':');
            if (separator < 1) return;
            const label = document.createElement('strong');
            const value = document.createElement('span');
            label.textContent = text.slice(0, separator);
            value.textContent = text.slice(separator + 1).trim();
            item.textContent = '';
            item.append(label, value);
          });
        }

        if (kind === 'wearers') {
          const lists = descriptionBlock.querySelectorAll(':scope > ul');
          const worthHeading = Array.from(descriptionBlock.querySelectorAll(':scope > p'))
            .find((paragraph) => paragraph.textContent.trim().toLowerCase() === 'worth knowing');
          if (heading && lists.length > 1 && worthHeading) {
            const split = document.createElement('div');
            split.className = 'oc-description-split';
            const positive = document.createElement('section');
            positive.className = 'oc-description-subpanel oc-description-subpanel--positive';
            const caution = document.createElement('section');
            caution.className = 'oc-description-subpanel oc-description-subpanel--caution';
            worthHeading.classList.add('oc-description-subheading');
            worthHeading.setAttribute('role', 'heading');
            worthHeading.setAttribute('aria-level', '3');
            positive.append(heading, lists[0]);
            caution.append(worthHeading, lists[1]);
            split.append(positive, caution);
            descriptionBlock.appendChild(split);
          }
        }

        const iconByKind = {
          smell: 'drop',
          notes: 'star',
          family: 'seal',
          longevity: 'check',
          wear: 'globe',
          wearers: 'user',
          similar: 'search',
          authenticity: 'shield',
          details: 'seal'
        };
        const iconName = iconByKind[kind];
        const currentHeading = descriptionBlock.querySelector('h2');
        const template = iconName ? document.querySelector(`[data-oc-description-icon="${iconName}"]`) : null;
        const icon = template?.content.firstElementChild?.cloneNode(true);
        if (currentHeading && icon && !currentHeading.closest('.oc-description-block__head')) {
          const headingRow = document.createElement('div');
          headingRow.className = 'oc-description-block__head';
          const iconShell = document.createElement('span');
          iconShell.className = 'oc-description-icon';
          iconShell.setAttribute('aria-hidden', 'true');
          iconShell.appendChild(icon);
          currentHeading.before(headingRow);
          headingRow.append(iconShell, currentHeading);
        }

        content.appendChild(descriptionBlock);
      });
    });
  };

  const enhanceProductImages = (root = document) => {
    root.querySelectorAll('[data-oc-product-image]').forEach((image) => {
      if (image.dataset.backgroundDetectionReady === 'true') return;
      image.dataset.backgroundDetectionReady = 'true';

      let lastSource = '';
      const detectBackground = () => {
        const source = image.currentSrc || image.src;
        if (!source || source === lastSource) return;
        lastSource = source;

        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.decoding = 'async';
        probe.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const size = 32;
            canvas.width = size;
            canvas.height = size;
            const context = canvas.getContext('2d', { willReadFrequently: true });
            context.drawImage(probe, 0, 0, size, size);
            const pixels = context.getImageData(0, 0, size, size).data;
            let light = 0;
            let sampled = 0;
            for (let y = 0; y < size; y += 1) {
              for (let x = 0; x < size; x += 1) {
                if (x > 4 && x < size - 5 && y > 4 && y < size - 5) continue;
                const offset = (y * size + x) * 4;
                const red = pixels[offset];
                const green = pixels[offset + 1];
                const blue = pixels[offset + 2];
                const alpha = pixels[offset + 3];
                const spread = Math.max(red, green, blue) - Math.min(red, green, blue);
                sampled += 1;
                if (alpha < 20 || (red > 238 && green > 238 && blue > 238 && spread < 16)) light += 1;
              }
            }
            image.dataset.plainLightBackground = String(light / sampled > 0.72);
          } catch (error) {
            image.dataset.plainLightBackground = 'false';
          }
        };
        probe.src = source;
      };

      image.addEventListener('load', detectBackground);
      new MutationObserver(detectBackground).observe(image, {
        attributes: true,
        attributeFilter: ['src', 'srcset']
      });
      detectBackground();
    });
  };

  initializeTabs();
  initializeVariantChoices();
  enhanceProductDescriptions();
  enhanceProductImages();

  document.addEventListener('click', async (event) => {
    const quickAdd = event.target instanceof Element ? event.target.closest('[data-oc-quickadd]') : null;
    if (!quickAdd) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const variantId = Number(quickAdd.dataset.variantId);
    if (!variantId || quickAdd.disabled) return;

    const originalLabel = quickAdd.innerHTML;
    const originalAriaLabel = quickAdd.getAttribute('aria-label');
    quickAdd.disabled = true;
    quickAdd.dataset.ocState = 'loading';
    quickAdd.setAttribute('aria-busy', 'true');
    quickAdd.setAttribute('aria-label', 'Adding fragrance to cart');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: ['cart-drawer-section'],
          sections_url: window.location.pathname
        })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this fragrance.');

      quickAdd.dataset.ocState = 'success';
      quickAdd.removeAttribute('aria-busy');
      quickAdd.setAttribute('aria-label', 'Added to cart');
      window.OSMODIGrowth?.replaceDrawer?.(payload.sections?.['cart-drawer-section'], {
        preserveRecommendations: true,
        preserveScroll: true
      });
      await window.OSMODIGrowth?.openDrawer?.();
      const cart = await window.OSMODIGrowth?.fetchCart?.();
      if (cart) window.OSMODIGrowth?.updateCount?.(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, payload } }));
      window.setTimeout(() => {
        if (!quickAdd.isConnected) return;
        quickAdd.innerHTML = originalLabel;
        quickAdd.disabled = false;
        quickAdd.removeAttribute('aria-busy');
        if (originalAriaLabel) quickAdd.setAttribute('aria-label', originalAriaLabel);
        else quickAdd.removeAttribute('aria-label');
        delete quickAdd.dataset.ocState;
      }, 1200);
    } catch (error) {
      quickAdd.dataset.ocState = 'error';
      quickAdd.removeAttribute('aria-busy');
      quickAdd.setAttribute('aria-label', error instanceof Error ? error.message : 'Unable to add this fragrance');
      quickAdd.innerHTML = '!';
      window.setTimeout(() => {
        quickAdd.innerHTML = originalLabel;
        quickAdd.disabled = false;
        if (originalAriaLabel) quickAdd.setAttribute('aria-label', originalAriaLabel);
        else quickAdd.removeAttribute('aria-label');
        delete quickAdd.dataset.ocState;
      }, 1600);
    }
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    initializeTabs(event.target);
    initializeVariantChoices(event.target);
    enhanceProductDescriptions(event.target);
    enhanceProductImages(event.target);
  });
})();
